import { STORAGE_KEYS, readStored, removeStored, writeStored } from "@/lib/storage"

/**
 * The cross-tab refresh lock where the browser has no Web Locks (LLD §6.2,
 * Task 1.2 fix round 1): `navigator.locks` exists only in secure contexts, and
 * a standalone deployment on a LAN may be served over plain http.
 *
 * A lease in localStorage: `{owner, until}`, an owner id and an expiry,
 * never a token. A tab takes a free or expired lease, reads it back to check
 * it won, and releases it when done. A tab that finds the lease taken polls
 * until it is free. The TTL outlasts the refresh's own 10 s timeout, so a tab
 * that dies holding the lease blocks the others for 15 s at most; a waiter
 * gives up waiting after the same time and refreshes anyway.
 */
const TTL_MS = 15_000
const POLL_MS = 50

interface Lease {
  owner: string
  until: number
}

function read(): Lease | null {
  const raw = readStored(STORAGE_KEYS.refreshLease)
  if (!raw) return null
  try {
    return JSON.parse(raw) as Lease
  } catch {
    return null
  }
}

function tryTake(owner: string): boolean {
  const now = Date.now()
  const held = read()
  if (held && held.owner !== owner && held.until > now) return false
  writeStored(STORAGE_KEYS.refreshLease, JSON.stringify({ owner, until: now + TTL_MS }))
  return read()?.owner === owner
}

export async function withRefreshLease<T>(fn: () => Promise<T>): Promise<T> {
  const owner = crypto.randomUUID()
  const giveUpAt = Date.now() + TTL_MS
  let waited = false
  while (!tryTake(owner) && Date.now() < giveUpAt) {
    waited = true
    await new Promise((r) => setTimeout(r, POLL_MS))
  }
  // The holder broadcast its token before releasing; one more task lets that
  // message land before `fn` decides whether to adopt it.
  if (waited) await new Promise((r) => setTimeout(r, 0))
  try {
    return await fn()
  } finally {
    if (read()?.owner === owner) removeStored(STORAGE_KEYS.refreshLease)
  }
}
